import type { SessionService } from './session-service';

/** A tab switch is cheap to observe but a whoami is not free; one check per window. */
const MIN_CHECK_INTERVAL_MS = 30_000;

const WATCHED_EVENTS = ['visibilitychange', 'focus'] as const;

export interface SessionWatchdogOptions {
  session: SessionService;
  target?: EventTarget;
  isVisible?: () => boolean;
  now?: () => number;
  minIntervalMs?: number;
}

/**
 * Re-checks the session when the tab comes back (PSTUDIO-4022 was reported on idle
 * tabs). Nothing else notices a Kratos TTL expiry: the gateway keeps answering with
 * the `anonymous` role, and the `hasActiveSessions` subscription only reflects the
 * backend's own inactivity/lifespan jobs.
 */
export function watchSessionVisibility({
  session,
  target = window,
  isVisible = () => document.visibilityState === 'visible',
  now = () => Date.now(),
  minIntervalMs = MIN_CHECK_INTERVAL_MS,
}: SessionWatchdogOptions): () => void {
  let lastCheckedAt: number | undefined;

  const check = () => {
    if (!isVisible()) return;

    const timestamp = now();

    if (
      lastCheckedAt !== undefined &&
      timestamp - lastCheckedAt < minIntervalMs
    ) {
      return;
    }

    lastCheckedAt = timestamp;

    void session.revalidate().then((status) => {
      // A check that learned nothing must not consume the window — the first
      // request after a laptop wakes commonly fails on DNS/network, and the tab
      // would then wait for another focus that may never come.
      if (status.state === 'unknown') lastCheckedAt = undefined;
    });
  };

  WATCHED_EVENTS.forEach((event) => target.addEventListener(event, check));

  return () => {
    WATCHED_EVENTS.forEach((event) => target.removeEventListener(event, check));
  };
}
